import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { queryCache } from 'react-query';

import { useRouter } from '../application/Router';
import { generateRedirectUrl, normalizePathname } from '../utils/url-helpers';

export enum UserState {
  LoggedOut = 'loggedout',
  LoggedIn = 'loggedin',
  SessionExpired = 'expired',
  Forbidden = 'forbidden',
}

type Auth = {
  /** Undefined until we know for sure if the user logged in cookie is set. */
  userState?: UserState;
  /** URL to send the user to when they need to login */
  loginUrl?: string;
  /** URL to send the user to when they need to logout */
  logoutUrl?: string;
  /** Logout URL before client-side auth state has been verified. */
  authenticatedLogoutUrl?: string;
  /** update the user state */
  updateUserState?: (state: UserState) => void;
  /** Whether userdata has confirmed an authenticated session on this page. */
  hasValidatedSession?: boolean;
  /** Record that userdata confirmed an authenticated session. */
  markSessionValidated?: () => void;
};

export const AuthContext = createContext<Auth>({
  // Default to a logged out state if trying to render component outside the AuthContext
  userState: UserState.LoggedOut,
  hasValidatedSession: false,
});

type Props = {
  children: React.ReactNode;
  /** Name of cookie that should exist if the user is authenticated */
  authCookie?: string;
  /** Url to send the user to when they want to log in */
  loginUrl?: string;
  /** Url to send the user to when logging out */
  logoutUrl?: string;
  /** @internal */
  initialToken?: string;
};

export type AuthUrls = {
  loginUrl?: string;
  logoutUrl?: string;
};

function getAuthUrls(
  loginUrl?: string,
  logoutUrl?: string,
  redirectUrl?: string,
): AuthUrls {
  return {
    // Format the auth urls so they contain the current redirect URL
    loginUrl: loginUrl && generateRedirectUrl(loginUrl, redirectUrl),
    logoutUrl: logoutUrl && generateRedirectUrl(logoutUrl, redirectUrl),
  };
}

export function AuthProvider({
  loginUrl,
  logoutUrl,
  authCookie,
  initialToken,
  children,
}: Props) {
  const { pathname, search } = useRouter();
  const [userState, setUserState] = useState<UserState | undefined>(undefined);
  const [hasValidatedSession, setHasValidatedSession] = useState(false);
  const [urls, setUrls] = useState<AuthUrls>(() =>
    // The server only receives the pathname. Include browser query parameters
    // in the effect below, after the matching initial render has hydrated.
    getAuthUrls(loginUrl, logoutUrl, normalizePathname(pathname) || '/'),
  );

  useEffect(() => {
    if (!authCookie) return;
    // Check if the Cookie exists. If it does, assume the user is logged in.
    if (initialToken) {
      Cookies.set(authCookie, initialToken);
    }

    const loggedIn = !!Cookies.get(authCookie);

    setUserState((state) => {
      if (state) return state;
      return loggedIn ? UserState.LoggedIn : UserState.LoggedOut;
    });
    if (process.env.STORYBOOK) {
      // Refresh the userdata in the storybook, since it can change value.
      queryCache.invalidateQueries('user');
    }

    return () => {
      if (initialToken) {
        Cookies.remove(authCookie);
      }
    };
  }, [authCookie, initialToken]);

  useEffect(() => {
    setUrls(getAuthUrls(loginUrl, logoutUrl, `${pathname}${search || ''}`));
  }, [pathname, search, loginUrl, logoutUrl]);

  return (
    <AuthContext.Provider
      value={{
        userState,
        // Ensure the user can't login again, if they are already logged in.
        // This could be an issue when we interpolate the login URL in rich text fields.
        loginUrl: userState !== UserState.LoggedIn ? urls.loginUrl : '/',
        logoutUrl: userState !== UserState.LoggedOut ? urls.logoutUrl : '/',
        authenticatedLogoutUrl: urls.logoutUrl,
        hasValidatedSession,
        markSessionValidated: () => setHasValidatedSession(true),
        updateUserState: (state) => {
          if (
            state === UserState.SessionExpired ||
            state === UserState.LoggedOut
          ) {
            if (authCookie) Cookies.remove(authCookie);
            document.documentElement.removeAttribute('data-auth-logged-in');
            setHasValidatedSession(false);
          }
          setUserState(state);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

useAuth.displayName = 'useAuth';
